import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useTheme, normalizeThemePreference } from './ThemeContext';
import {
  UserPreferencesService,
  type UserPreferences,
} from '../services/api/userPreferencesService';
import { setAnalyticsDataSharingApproved } from '../utils/analytics';
import { syncNativePushRegistration } from '../services/push/nativePushService';
import { isNativeUi } from '../utils/nativeUi';

interface UserPreferencesContextValue {
  preferences: UserPreferences | null;
  refreshPreferences: () => Promise<void>;
  isLoading: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(
  undefined
);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const { preference: themePreference, setTheme } = useTheme();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const themeHydratedForUser = useRef<string | null>(null);
  const skipNextThemePersist = useRef(false);

  const refreshPreferences = useCallback(async () => {
    if (!currentUser?.id) {
      setPreferences(null);
      setAnalyticsDataSharingApproved(true);
      themeHydratedForUser.current = null;
      return;
    }
    setIsLoading(true);
    try {
      const prefs = await UserPreferencesService.getUserPreferences(currentUser.id);
      setPreferences(prefs);
      setAnalyticsDataSharingApproved(prefs?.data_sharing !== false);
    } catch (e) {
      console.error('[UserPreferences] 載入失敗:', e);
      setPreferences(null);
      setAnalyticsDataSharingApproved(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!currentUser?.id) {
        setPreferences(null);
        setAnalyticsDataSharingApproved(true);
        themeHydratedForUser.current = null;
        return;
      }

      setIsLoading(true);
      try {
        const prefs = await UserPreferencesService.getUserPreferences(currentUser.id);
        if (cancelled) return;
        setPreferences(prefs);
        setAnalyticsDataSharingApproved(prefs?.data_sharing !== false);

        const t = prefs?.appearance_theme;
        if (t) {
          skipNextThemePersist.current = true;
          setTheme(normalizeThemePreference(t));
        }
        themeHydratedForUser.current = currentUser.id;

        if (isNativeUi() && prefs?.push_notifications !== false) {
          void syncNativePushRegistration(currentUser.id);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[UserPreferences] 載入失敗:', e);
          setPreferences(null);
          setAnalyticsDataSharingApproved(true);
          themeHydratedForUser.current = currentUser.id;
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, setTheme]);

  useEffect(() => {
    if (!currentUser?.id) return;
    if (themeHydratedForUser.current !== currentUser.id) return;
    if (skipNextThemePersist.current) {
      skipNextThemePersist.current = false;
      return;
    }

    const timer = window.setTimeout(() => {
      UserPreferencesService.createOrUpdatePreferences(currentUser.id, {
        appearance_theme: themePreference,
      }).catch((err) => console.warn('[UserPreferences] 同步主題失敗:', err));
    }, 450);

    return () => window.clearTimeout(timer);
  }, [themePreference, currentUser?.id]);

  const value = useMemo<UserPreferencesContextValue>(
    () => ({
      preferences,
      refreshPreferences,
      isLoading,
    }),
    [preferences, refreshPreferences, isLoading]
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return ctx;
}
